import BlotFormatter from "quill-blot-formatter";
import ImageResize from "quill-image-resize-module-react";
import React, { useEffect, useMemo, useRef } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import styled from "styled-components";
import CommonApi from "../../../api/common.api";
import { errorHandler } from "../../../util/errorHandler";
import Loading from "../../templates/Loading";
import PretendardText from "../text/pretendardText";
import { imgUrl } from "../../../constants/url";

Quill.register("modules/blotFormatter", BlotFormatter);
Quill.register("modules/imageResize", ImageResize);

function TextFormEditor({
  type,
  title,
  placeholder,
  value,
  onChange,
  style,
  editorStyle,
  editorHeight,
  disabled,
  position,
  isBlur,
  isLoading,
  setIsLoading,
}) {
  const quillRef = useRef();
  const imageUploadingRef = useRef(false);

  const handleImage = () => {
    if (imageUploadingRef.current || disabled) return;

    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    // 파일 선택창에서 포커스가 빠지기 전에 삽입 위치를 저장한다.
    const selectedRange = editor.getSelection(true);
    const insertIndex = selectedRange?.index ?? Math.max(0, editor.getLength() - 1);

    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/jpeg,image/png");
    input.click();

    input.addEventListener("change", async () => {
      const file = input.files?.[0];
      if (!file) return;

      if (!["image/jpeg", "image/png"].includes(file.type)) {
        toast("JPG 또는 PNG 이미지만 첨부할 수 있습니다.");
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast("에디터 이미지는 10MB 이하만 첨부할 수 있습니다.");
        return;
      }

      imageUploadingRef.current = true;
      setIsLoading?.(true);

      try {
        const imageUrl = await fileAddFunc(file);
        if (!imageUrl) throw new Error("이미지 URL을 받지 못했습니다.");

        editor.insertEmbed(insertIndex, "image", imageUrl, "user");
        editor.setSelection(insertIndex + 1, 0, "silent");
      } catch (error) {
        errorHandler(error);
      } finally {
        imageUploadingRef.current = false;
        setIsLoading?.(false);
      }
    });
  };

  const modules = useMemo(() => {
    return {
      toolbar: {
        container: [
          [{ size: ["small", false, "large", "huge"] }], // custom dropdown
          ["bold", "italic", "underline", "strike", "blockquote"],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ list: "ordered" }, { list: "bullet" }],
          [
            { align: "" },
            { align: "center" },
            { align: "right" },
            { align: "justify" },
          ],
          ["link", "image", "video"],
          ["clean"],
        ],

        imageResize: {
          parchment: Quill.import("parchment"),
          modules: ["Resize", "DisplaySize"],
        },
        clipboard: {
          // toggle to add extra line breaks when pasting HTML:
          matchVisual: false,
        },
        handlers: {
          // 이미지 처리는 우리가 직접 imageHandler라는 함수로 처리할 것이다.
          image: handleImage,
          // video: videoHandler,
        },
      },
      blotFormatter: {},
    };
  }, []);

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "size",
    "color",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "video",
    "align",
  ];

  const fileAddFunc = async (file) => {
    const formData = new FormData();
    formData.append("path", "editor");
    formData.append("file", file);

    const response = (await CommonApi.fileUpload(formData)).data.data;

    return response?.serverFileName
      ? imgUrl + response.serverFileName
      : null;
  };

  useEffect(() => {
    if (isBlur) {
      quillRef.current.blur();
    }
  }, [position]);

  return (
    <>
      <TextFormWrapper
        style={{
          height: editorHeight,
          ...style,
        }}
      >
        {/* <TextFormTitle type={type}>{title}</TextFormTitle> */}
        <div
          className="text-editor"
          style={{
            width: "100%",
            height: editorHeight,
            ...editorStyle,
          }}
        >
          <ReactQuill
            theme="snow"
            ref={quillRef}
            modules={modules}
            formats={formats}
            value={value || ""}
            onChange={onChange}
            style={{
              height: editorHeight - 40,
            }}
            placeholder={placeholder}
          />
          {isLoading ? <Loading /> : null}
        </div>
      </TextFormWrapper>
    </>
  );
}

export default TextFormEditor;

const TextFormWrapper = styled.div.attrs((props) => {})`
  display: flex;
  gap: 20px;
  height: 48px;
  margin-bottom: 24px;
`;

const TextFormTitle = styled(PretendardText).attrs((props) => {})`
  display: block;
  min-width: ${(props) => (props.type === "edit" ? "132px" : "200px")};
  color: var(--gray-4, #adb4ba);
  font-size: 15px;
  font-style: normal;
  font-weight: 600;
  line-height: 24.5px;
`;
